import InfiniteScroll from "react-infinite-scroll-component";
import DiscoverCourseCard from "../../components/cards/DiscoverCourseCard";
import SelectFilter from "../../components/inputs/SelectFilter";
import SearchFilter from "../../components/inputs/SearchFilter";
import CourseDetailsModal from "../../components/Modals/CourseDetailsModal";
import LearningPathsModal from "../../components/Modals/LearningPathsModal";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyState from "../../components/EmptyState";
import Icon from "../../components/icons/Icon";
import { LearningPathsStrokeIcon } from "../../components/icons/Icons";
import {
  useLazyListCoursesQuery,
  useLazyListTopicsQuery,
} from "../../services/organization/adminCourse";
import { useState, useEffect, useMemo } from "react";
import useCoursesFilter from "../../hooks/useCoursesFilter";
import { useTranslation } from "react-i18next";

const DiscoverCourses = () => {
  const [queryParams, setTopic, setLevel, setSearch, topic, level] =
    useCoursesFilter();
  const [allCourses, setAllCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseToAdd, setCourseToAdd] = useState(null);

  const { t } = useTranslation(["common", "employees", "learningPaths"]);

  const allTopicOption = useMemo(
    () => ({ value: "", label: t("all") }),
    [t]
  );

  const levelOptions = useMemo(
    () => [
      { value: "", label: t("all"), icon: "icon1" },
      { value: "beginner", label: t("levels.beginner"), icon: "icon2" },
      { value: "intermediate", label: t("levels.intermediate"), icon: "icon3" },
      { value: "advanced", label: t("levels.advanced"), icon: "icon4" },
    ],
    [t]
  );

  const [fetchCourses, { data: fetchedCourses, isLoading }] =
    useLazyListCoursesQuery({
      page,
    });
  const [fetchTopics, { data: topicsData }] = useLazyListTopicsQuery();

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    fetchCourses({ page, filters: queryParams });
  }, [page]);

  useEffect(() => {
    fetchCourses({ page: 1, filters: queryParams });
    setPage(1);
  }, [queryParams]);

  useEffect(() => {
    if (fetchedCourses?.data?.length) {
      if (fetchedCourses?.pagination?.current == 1) {
        setAllCourses(fetchedCourses?.data);
      } else {
        setAllCourses((prev) => {
          return [...prev, ...fetchedCourses?.data];
        });
      }
    } else {
      setAllCourses([]);
    }
  }, [fetchedCourses]);

  const tagesOptions = useMemo(
    () => [
      allTopicOption,
      ...(topicsData?.data?.map(({ slug, title }) => ({
        value: slug,
        label: title,
      })) ?? []),
    ],
    [topicsData, allTopicOption]
  );

  const selectedTopicOption = useMemo(
    () =>
      tagesOptions.find((option) => option.value === topic) ?? allTopicOption,
    [tagesOptions, topic, allTopicOption]
  );

  const selectedLevelOption = useMemo(
    () =>
      levelOptions.find((option) => option.value === level) ?? levelOptions[0],
    [levelOptions, level]
  );

  const handleAddToLearningPath = (course) => {
    setCourseToAdd(course);
  };

  const topicFilterOnchange = (data) => setTopic(data.value);
  const levelFilterOnchange = (data) => setLevel(data.value);
  const searchFilterOnchange = (data) => setSearch(data.value);

  if (isLoading && page === 1 && allCourses.length === 0) {
    return <LoadingIndicator />;
  }

  return (
    <div className="discover-courses-page">
      <header className="discover-courses-page__toolbar">
        <div className="discover-courses-page__intro">
          <h1 className="title-page mb-0">
            {t("learningPaths:navigateCourses")}
          </h1>
          <p className="mb-0 font_18 font-color-grey mt-2">
            {t("learningPaths:addCoursesHint")}
          </p>
        </div>
      </header>

      <div className="discover-courses-page__filters nav-filters">
        <div className="row row-cols-1 row-cols-md-3 g-3">
          <div className="col">
            <SearchFilter
              label={t("search")}
              placeholder={t("learningPaths:searchForCourse")}
              onchange={searchFilterOnchange}
              className="discover-courses-page__search-filter"
            />
          </div>
          <div className="col">
            <SelectFilter
              label={t("topics")}
              options={tagesOptions}
              value={selectedTopicOption}
              onchange={topicFilterOnchange}
            />
          </div>
          <div className="col">
            <SelectFilter
              label={t("level")}
              options={levelOptions}
              value={selectedLevelOption}
              hasIcon
              onchange={levelFilterOnchange}
            />
          </div>
        </div>
      </div>

      <div className="discover-courses-page__grid discover-courses">
        {Boolean(allCourses.length) ? (
          <InfiniteScroll
            dataLength={allCourses?.length || 0}
            next={() => setPage((pageNo) => pageNo + 1)}
            hasMore={!!fetchedCourses?.pagination?.next}
            loader={
              <h4 className="text-center mt-3 discover-courses-page__loader">
                {t("Loading")}
              </h4>
            }
            endMessage={
              <p className="discover-courses-page__end-message text-center mb-0">
                <b>{t("successfulCoursesDisplay")}</b>
              </p>
            }
          >
            <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-3 discover-courses-page__cards-row">
              {allCourses?.map((courseInfo) => {
                return (
                  <DiscoverCourseCard
                    key={courseInfo?.id}
                    imageUrl={courseInfo?.image_url}
                    courseTitle={courseInfo?.title}
                    userName={courseInfo.tutor?.name || ""}
                    courseTag={courseInfo.topics?.[0]}
                    color={courseInfo?.topics?.[0]?.color}
                    avatarUrl={courseInfo?.tutor?.avatar_url}
                    courseDuration={courseInfo?.video_duration}
                    courseLessons={courseInfo?.lessons_count}
                    addLearningTrack
                    onClick={() => setSelectedCourse(courseInfo)}
                    onAddToPath={() => handleAddToLearningPath(courseInfo)}
                  />
                );
              })}
            </div>
          </InfiniteScroll>
        ) : (
          <div className="discover-courses-page__empty">
            <EmptyState
              iconComponent={
                <Icon
                  icon={<LearningPathsStrokeIcon />}
                  hasStroke
                  iconName="learning-path-stroke"
                  iconColor="#bbbabf"
                  iconSize={38}
                  viewBox="0 0 23 19"
                />
              }
              title={t("emptyStates.courses")}
              customTitle={t("noMatching")}
              emptyStateText=" "
            />
          </div>
        )}
      </div>

      <CourseDetailsModal
        course={selectedCourse}
        isOpened={!!selectedCourse}
        onClose={() => setSelectedCourse(null)}
      />

      {courseToAdd && (
        <LearningPathsModal
          isOpened={!!courseToAdd}
          onClose={() => setCourseToAdd(null)}
          course={courseToAdd}
        />
      )}
    </div>
  );
};

export default DiscoverCourses;
